/** @jsxImportSource theme-ui */
import React from 'react';
import { Divider, Grid } from 'semantic-ui-react';
import Shiitake from 'shiitake';

import { formatDateISO, formatDateOrRangeShort, getDatePartStr } from 'utils/dateTimeHelpers';
import { formatNumber } from 'utils/stringFormattingHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { IClickable } from 'components/_common/common-props';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { IconButton } from 'components/_controls/IconButton';
import {
  DiseaseActivityDatumType,
  DiseaseActivityDatumUpdate,
} from 'components/_graphs/_common/DiseaseActivityModels';

// =====================================================================================================================================

type DiseaseActivityPopupHeaderDateProps = {
  datum: DiseaseActivityDatumType;
  showCloseButton: boolean;
  onClose: () => void;
};

export const DiseaseActivityPopupHeaderDate: React.FC<DiseaseActivityPopupHeaderDateProps> = ({
  datum,
  showCloseButton,
  onClose,
}) => (
  <div sx={{ position: 'relative' }}>
    <Typography variant="subtitle2" color="white" marginBottom="12px">
      {formatDateOrRangeShort(datum.date, datum.dateEnd)}
    </Typography>
    {showCloseButton && (
      <IconButton
        ariaLabel="Close disease activity popup"
        icon="icon-close"
        color="white"
        size="medium"
        onClick={() => onClose()}
        sx={{ position: 'absolute', top: 0, right: 0 }}
      />
    )}
  </div>
);

// =====================================================================================================================================

type DiseaseActivityPopupCaseCountsProps = {
  valueSeries1: number;
  valueSeries2: number;
  labelSeries1: string;
  labelSeries2: string;
};

export const DiseaseActivityPopupCaseCounts: React.FC<DiseaseActivityPopupCaseCountsProps> = ({
  valueSeries1,
  valueSeries2,
  labelSeries1,
  labelSeries2,
}) => (
  <Grid columns={2} divided className="dark-bg">
    <Grid.Row>
      <Grid.Column>
        <Typography variant="caption" color="stone50" nowrap>
          {labelSeries1}
        </Typography>
        <Typography
          variant="subtitle2"
          color="white"
          marginBottom="0"
          nowrap
          sx={{ opacity: 0.93 }}
        >
          {formatNumber(valueSeries1, null, null, 'emdash-0')}
          {valueSeries1 < 0 && (
            <BdIcon name="icon-asterisk" color="deepSea40" bold sx={{ verticalAlign: 'middle' }} />
          )}
        </Typography>
      </Grid.Column>
      <Grid.Column>
        <Typography variant="caption" color="stone50" nowrap>
          {labelSeries2}
        </Typography>
        <Typography
          variant="subtitle2"
          color="white"
          marginBottom="0"
          nowrap
          sx={{ opacity: 0.93 }}
        >
          {formatNumber(valueSeries2, null, null, 'emdash-0')}
          {valueSeries2 < 0 && (
            <BdIcon name="icon-asterisk" color="deepSea40" bold sx={{ verticalAlign: 'middle' }} />
          )}
        </Typography>
      </Grid.Column>
    </Grid.Row>
  </Grid>
);

// =====================================================================================================================================

type DiseaseActivityPopupUpdatesPreviewProps = {
  topUpdate: DiseaseActivityDatumUpdate;
};

export const DiseaseActivityPopupUpdatesPreview: React.FC<
  DiseaseActivityPopupUpdatesPreviewProps
> = ({ topUpdate }) => (
  <>
    <Divider sx={{ '&.ui.divider': { mt: '16px', mb: 0 } }} />
    <div sx={{ position: 'relative' }}>
      <DiseaseActivityPopupUpdateSection update={topUpdate} dontShowLink />
      <div
        sx={{
          position: 'absolute',
          width: '100%',
          height: '20px',
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(37, 40, 56, 0) 0%, #252838 100%);',
        }}
      />
    </div>
  </>
);

// =====================================================================================================================================

type DiseaseActivityPopupAllUpdatesProps = {
  topUpdates: DiseaseActivityDatumUpdate[];
  totalUpdatesCount: number;
  callbackClosePopup: () => void;
  onGoToActivityHistory: (activityDate?: string) => void;
};

export const DiseaseActivityPopupAllUpdates: React.FC<DiseaseActivityPopupAllUpdatesProps> = ({
  topUpdates,
  totalUpdatesCount,
  callbackClosePopup,
  onGoToActivityHistory,
}) =>
  totalUpdatesCount > 0 &&
  topUpdates?.length > 0 && (
    <>
      <Divider sx={{ '&.ui.divider': { my: '16px' } }} />
      {topUpdates.map((update, i) => (
        <DiseaseActivityPopupUpdateSection
          key={`popup-update${i}`}
          update={update}
          onClick={() => {
            onGoToActivityHistory(getDatePartStr(update.date));
            callbackClosePopup();
          }}
        />
      ))}
      {totalUpdatesCount > 2 && (
        <Typography
          variant="body2"
          color="white"
          sx={{ cursor: 'pointer', textDecoration: 'underline', mt: '10px' }}
          onClick={() => {
            onGoToActivityHistory(getDatePartStr(topUpdates[0].date));
            callbackClosePopup();
          }}
        >
          See {totalUpdatesCount} situational updates
        </Typography>
      )}
    </>
  );

// =====================================================================================================================================

type DiseaseActivityPopupUpdateSectionProps = IClickable & {
  update: DiseaseActivityDatumUpdate;
  dontShowLink?: boolean;
};

export const DiseaseActivityPopupUpdateSection: React.FC<
  DiseaseActivityPopupUpdateSectionProps
> = ({ onClick, update, dontShowLink = false }) => {
  return (
    <div sx={{ mt: '16px' }}>
      <div sx={{ mb: '2px' }}>
        <Typography variant="overline" color="stone50" inline>
          {formatDateISO(update.date)}
          {' | '}
        </Typography>
        <Typography
          variant="overline"
          color={update.isLocationNearby ? 'seafoam70' : 'stone50'}
          inline
        >
          {update.locationName}
        </Typography>
      </div>
      <Typography variant="body2" color="white">
        <FlexGroup alignItems="flex-end">
          {update.isRawHtml ? (
            <div dangerouslySetInnerHTML={{ __html: update.text }} />
          ) : (
            <Shiitake lines={2} throttleRate={200} className="shiitake">
              {update.text}
            </Shiitake>
          )}
          {!dontShowLink && (
            <div sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={onClick}>
              Read more
            </div>
          )}
        </FlexGroup>
      </Typography>
    </div>
  );
};

// =====================================================================================================================================

export const DiseaseActivityPopupFooterNegativeValues: React.FC = () => (
  <div sx={{ mt: '16px' }}>
    <Typography variant="caption" color="stone10">
      <BdIcon name="icon-asterisk" color="deepSea40" bold sx={{ verticalAlign: 'middle' }} />{' '}
      Negative values are likely due to corrections made to previous reported case and death counts
    </Typography>
  </div>
);
